import { Form, Head, Link, router, usePage } from '@inertiajs/react'
import type { FormEvent } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { TrafficWorldMap } from '@/components/admin/TrafficWorldMap'
import type { SharedInertiaProps } from '@/types/inertiaShared'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

type UniqueVisitorRow = { bucket: string; count: number }
type TopPathRow = { path: string; count: number }
type LogRow = {
  id: number
  occurredAt: string
  path: string
  kind: string
  method: string
  ip: string
  /** GeoLite2-derived label at request time; empty when DB missing or non-public IP. */
  location: string | null
  /** Full visitor fingerprint hex; use for log search. */
  fingerprint: string
  fingerprintPreview: string
  userId: number | null
}

type Filters = {
  from: string
  to: string
  pathQ: string
  kind: string
  logQ: string
  /** When true, log table omits rows where user_id is 1 (guest rows still shown). */
  excludeUser1: boolean
  uniquesGranularity: 'day' | 'hour'
  logSort: string
  logDir: 'asc' | 'desc'
  page: number
  perPage: number
  total: number
  totalPages: number
}

type GeoPoint = {
  lat: number
  lon: number
  weight: number
  ip: string
  /** GeoLite location string; aligns with log Location when backfilled. */
  locationLabel?: string
  countryCode?: string
}

type PageProps = {
  uniqueVisitors: UniqueVisitorRow[]
  /** Distinct visitor fingerprints in the chart scope for the selected range and filters. */
  uniqueVisitorsTotal: number
  geoPoints: GeoPoint[]
  /** False when GeoLite2-City .mmdb is missing or unreadable. */
  geoAvailable: boolean
  topPaths: TopPathRow[]
  logs: LogRow[]
  filters: Filters
  kinds: string[]
} & Pick<SharedInertiaProps, 'flash'>

const LOG_SORT_COLUMNS = [
  { key: 'occurred_at', label: 'Time (UTC)' },
  { key: 'path', label: 'Path' },
  { key: 'kind', label: 'Kind' },
  { key: 'method', label: 'Method' },
  { key: 'ip', label: 'IP' },
  { key: 'location', label: 'Location' },
  { key: 'fingerprint', label: 'Fp' },
  { key: 'user', label: 'User' },
] as const

function trafficGetParams(filters: Filters, patch: Partial<Filters> = {}) {
  const f = { ...filters, ...patch }
  return {
    from: f.from,
    to: f.to,
    path_q: f.pathQ,
    kind: f.kind || undefined,
    log_q: f.logQ,
    // Inertia merges this into the current URL’s query string; omitting the key would leave an old
    // exclude_user_1=1 in place when turning the filter off, so the checkbox and server stay “stuck”.
    exclude_user_1: f.excludeUser1 ? '1' : '0',
    uniques_granularity: f.uniquesGranularity,
    log_sort: f.logSort,
    log_dir: f.logDir,
    page: f.page,
  }
}

function formatDayLabel(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatHourLabel(iso: string) {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function SortIndicator({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return <span className="ml-1 text-on-surface-variant/40">↕</span>
  return <span className="ml-1 text-primary">{dir === 'asc' ? '↑' : '↓'}</span>
}

/** Tracks a container's rendered width so chart dimensions (Recharts axis widths, label length) can adapt on mobile. */
function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)

  // useLayoutEffect (not useEffect) so the first measurement lands before paint — otherwise the
  // chart briefly renders at the wide default before ResizeObserver's first callback corrects it.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const measure = () => setWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return { ref, width }
}

export default function AdminTraffic() {
  const { uniqueVisitors, uniqueVisitorsTotal, geoPoints, geoAvailable, topPaths, logs, filters, kinds, flash } =
    usePage<PageProps>().props

  const { ref: topPathsRef, width: topPathsWidth } = useContainerWidth<HTMLDivElement>()
  const isNarrowChart = topPathsWidth > 0 && topPathsWidth < 480
  const topPathsYAxisWidth = isNarrowChart ? 96 : 200
  const topPathsLabelMax = isNarrowChart ? 20 : 48

  const [from, setFrom] = useState(filters.from)
  const [to, setTo] = useState(filters.to)
  const [pathQ, setPathQ] = useState(filters.pathQ)
  const [kind, setKind] = useState(filters.kind)
  const [logQDraft, setLogQDraft] = useState(filters.logQ)
  const logSearchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setFrom(filters.from)
    setTo(filters.to)
    setPathQ(filters.pathQ)
    setKind(filters.kind)
    setLogQDraft(filters.logQ)
  }, [filters.from, filters.to, filters.pathQ, filters.kind, filters.logQ])

  const chartGranularity = filters.uniquesGranularity

  const chartData = useMemo(
    () =>
      uniqueVisitors.map((row) => ({
        ...row,
        label: chartGranularity === 'hour' ? formatHourLabel(row.bucket) : formatDayLabel(row.bucket),
      })),
    [uniqueVisitors, chartGranularity],
  )

  const topPathsChart = useMemo(
    () =>
      topPaths.map((row) => ({
        ...row,
        shortPath:
          row.path.length > topPathsLabelMax ? `${row.path.slice(0, topPathsLabelMax - 2)}…` : row.path,
      })),
    [topPaths, topPathsLabelMax],
  )

  const visitOpts = { preserveState: true, preserveScroll: true, replace: true as const }

  function visitTraffic(patch: Partial<Filters>) {
    router.get('/admin/traffic', trafficGetParams(filters, patch), visitOpts)
  }

  function applyMainFilters(e: FormEvent) {
    e.preventDefault()
    visitTraffic({ from, to, pathQ, kind, page: 1 })
  }

  function applyLogSearch(e: FormEvent) {
    e.preventDefault()
    visitTraffic({ logQ: logQDraft, page: 1 })
  }

  function clearLogSearch() {
    setLogQDraft('')
    if (filters.logQ) {
      visitTraffic({ logQ: '', page: 1 })
    }
  }

  function goToPage(e: FormEvent, page: number) {
    e.preventDefault()
    visitTraffic({ page })
  }

  function setUniquesGranularity(next: 'day' | 'hour') {
    if (next === chartGranularity) return
    visitTraffic({ uniquesGranularity: next, page: 1 })
  }

  function onSortColumn(col: (typeof LOG_SORT_COLUMNS)[number]['key']) {
    let logDir: 'asc' | 'desc'
    if (filters.logSort === col) {
      logDir = filters.logDir === 'desc' ? 'asc' : 'desc'
    } else {
      logDir = col === 'occurred_at' ? 'desc' : 'asc'
    }
    visitTraffic({ logSort: col, logDir, page: 1 })
  }

  const filterLogsByMapLocation = useCallback(
    (query: string) => {
      setLogQDraft(query)
      visitTraffic({ logQ: query, page: 1 })
    },
    [filters],
  )

  return (
    <>
      <Head title="Traffic" />
      <div className="mx-auto max-w-6xl space-y-10 py-12 md:py-16">
        {flash?.alert ? (
          <p
            role="alert"
            className="rounded-md border border-red-500/35 bg-surface-container-high px-4 py-3 font-mono text-sm text-on-surface"
          >
            {flash.alert}
          </p>
        ) : null}
        {flash?.notice ? (
          <p className="rounded-md border border-primary-container/30 bg-surface-container-high px-4 py-3 font-mono text-sm text-on-surface">
            {flash.notice}
          </p>
        ) : null}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="font-mono text-xs font-bold tracking-widest text-primary uppercase">Admin</p>
            <h1 className="font-headline text-2xl font-bold tracking-tight text-on-surface">Traffic</h1>
            <p className="mt-3 font-headline text-3xl font-bold tabular-nums tracking-tight text-primary-container">
              {uniqueVisitorsTotal.toLocaleString()}
              <span className="ml-2 align-middle font-mono text-sm font-normal text-on-surface-variant">
                approx. unique visitors
              </span>
            </p>
            <p className="mt-2 max-w-2xl font-mono text-sm text-on-surface-variant">
              Count is distinct visitors across the selected range and chart filters (same basis as the graph). Uses a
              server-only fingerprint (hashed IP + User-Agent). Shared networks (NAT, offices) can make counts look
              lower than real people; treat as directional, not exact.
            </p>
          </div>
          <Link href="/admin" className="key-btn-secondary inline-flex shrink-0 justify-center">
            Back to dashboard
          </Link>
        </div>

        <form
          onSubmit={applyMainFilters}
          className="grid grid-cols-1 gap-4 rounded-xl border border-outline-variant/20 bg-surface-container-low p-4 md:grid-cols-12 md:items-end"
        >
          <div className="md:col-span-3">
            <label htmlFor="traffic-from" className="mb-1 block font-mono text-xs text-on-surface-variant">
              From
            </label>
            <input
              id="traffic-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded-lg border border-outline-variant/35 bg-surface-container-lowest px-3 py-2 font-mono text-sm text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-primary-container/40"
            />
          </div>
          <div className="md:col-span-3">
            <label htmlFor="traffic-to" className="mb-1 block font-mono text-xs text-on-surface-variant">
              To
            </label>
            <input
              id="traffic-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded-lg border border-outline-variant/35 bg-surface-container-lowest px-3 py-2 font-mono text-sm text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-primary-container/40"
            />
          </div>
          <div className="md:col-span-4">
            <label htmlFor="traffic-path" className="mb-1 block font-mono text-xs text-on-surface-variant">
              Path contains
            </label>
            <input
              id="traffic-path"
              type="search"
              value={pathQ}
              onChange={(e) => setPathQ(e.target.value)}
              placeholder="/about, /tools…"
              className="w-full rounded-lg border border-outline-variant/35 bg-surface-container-lowest px-3 py-2 font-mono text-sm text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-primary-container/40"
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="traffic-kind" className="mb-1 block font-mono text-xs text-on-surface-variant">
              Kind
            </label>
            <select
              id="traffic-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="w-full rounded-lg border border-outline-variant/35 bg-surface-container-lowest px-3 py-2 font-mono text-sm text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-primary-container/40"
            >
              <option value="">All</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end md:col-span-12">
            <button type="submit" className="key-btn-primary inline-flex justify-center px-6">
              Apply filters
            </button>
          </div>
        </form>

        <section className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <h2 className="font-headline text-lg font-semibold text-on-surface">
              Approx. unique visitors / {chartGranularity === 'hour' ? 'hour' : 'day'}
            </h2>
            <div
              className="inline-flex rounded-lg border border-outline-variant/25 bg-surface-container-low p-0.5 font-mono text-xs"
              role="group"
              aria-label="Chart bucket size"
            >
              <button
                type="button"
                onClick={() => setUniquesGranularity('day')}
                className={
                  chartGranularity === 'day'
                    ? 'rounded-md bg-primary-container px-3 py-1.5 font-semibold text-on-primary-container'
                    : 'rounded-md px-3 py-1.5 text-on-surface-variant hover:text-on-surface'
                }
              >
                By day
              </button>
              <button
                type="button"
                onClick={() => setUniquesGranularity('hour')}
                className={
                  chartGranularity === 'hour'
                    ? 'rounded-md bg-primary-container px-3 py-1.5 font-semibold text-on-primary-container'
                    : 'rounded-md px-3 py-1.5 text-on-surface-variant hover:text-on-surface'
                }
              >
                By hour
              </button>
            </div>
          </div>
          <div className="h-72 w-full rounded-xl border border-outline-variant/15 bg-surface-container-low p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: chartGranularity === 'hour' ? 8 : 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-outline-variant/20" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: chartGranularity === 'hour' ? 9 : 11 }}
                  interval={chartGranularity === 'hour' ? Math.max(0, Math.floor(chartData.length / 14) - 1) : 'preserveStartEnd'}
                  angle={chartGranularity === 'hour' ? -35 : 0}
                  textAnchor={chartGranularity === 'hour' ? 'end' : 'middle'}
                  height={chartGranularity === 'hour' ? 56 : 30}
                  className="text-on-surface-variant"
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} className="text-on-surface-variant" />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-surface-container-highest, #f5f5f5)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 8,
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  name="Uniques (approx.)"
                  stroke="var(--color-primary-container, #c76b00)"
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-headline text-lg font-semibold text-on-surface">Top paths</h2>
          <div
            ref={topPathsRef}
            className="h-96 w-full rounded-xl border border-outline-variant/15 bg-surface-container-low p-2"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topPathsChart} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-outline-variant/20" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="shortPath"
                  width={topPathsYAxisWidth}
                  tick={{ fontSize: isNarrowChart ? 9 : 10 }}
                />
                <Tooltip />
                <Bar
                  dataKey="count"
                  name="Requests"
                  fill="var(--color-primary-container, #c76b00)"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <TrafficWorldMap geoPoints={geoPoints} geoAvailable={geoAvailable} onSelectGeoLocation={filterLogsByMapLocation} />

        <section className="space-y-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1 space-y-1">
              <h2 className="font-headline text-lg font-semibold text-on-surface">Log</h2>
              <p className="max-w-xl font-mono text-xs leading-relaxed text-on-surface-variant">
                Rows with an empty Location were usually ingested before GeoIP was configured or could not be resolved.
                Use the button to queue a background job that fills missing values from GeoLite2 (large tables run in
                batches).
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <Form action="/admin/traffic/backfill_ip_locations" method="post">
                {({ processing }) => (
                  <button
                    type="submit"
                    disabled={processing || !geoAvailable}
                    className="key-btn-secondary inline-flex justify-center px-4 py-2 font-mono text-xs whitespace-nowrap disabled:opacity-40"
                  >
                    {processing ? 'Queuing…' : 'Backfill IP locations'}
                  </button>
                )}
              </Form>
              <p className="font-mono text-xs text-on-surface-variant sm:text-right">
                Page {filters.page} of {filters.totalPages} — {filters.total} rows
              </p>
            </div>
          </div>
          <form
            onSubmit={applyLogSearch}
            className="flex flex-col gap-3 rounded-xl border border-outline-variant/20 bg-surface-container-low p-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <div className="min-w-0 w-full flex-1 sm:min-w-[12rem]">
              <label htmlFor="traffic-log-q" className="mb-1 block font-mono text-xs text-on-surface-variant">
                Search logs (path, IP, location, kind, method, fingerprint, user id)
              </label>
              <input
                ref={logSearchInputRef}
                id="traffic-log-q"
                type="search"
                value={logQDraft}
                onChange={(e) => setLogQDraft(e.target.value)}
                placeholder="Filter rows in the table…"
                className="w-full rounded-lg border border-outline-variant/35 bg-surface-container-lowest px-3 py-2 font-mono text-sm text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-primary-container/40"
              />
            </div>
            <label
              htmlFor="traffic-exclude-user-1"
              className="flex cursor-pointer select-none items-center gap-2 font-mono text-xs text-on-surface-variant sm:mb-0.5"
            >
              <input
                id="traffic-exclude-user-1"
                type="checkbox"
                checked={filters.excludeUser1}
                onChange={(e) => visitTraffic({ excludeUser1: e.target.checked, page: 1 })}
                className="size-4 shrink-0 rounded border border-outline-variant/50 bg-surface-container-lowest text-primary-container checked:border-transparent checked:bg-primary-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-container/40"
              />
              Hide admin
            </label>
            <div className="flex shrink-0 flex-wrap gap-2 sm:ml-auto sm:self-end">
              <button type="submit" className="key-btn-primary inline-flex justify-center px-6">
                Search
              </button>
              <button
                type="button"
                onClick={clearLogSearch}
                disabled={!filters.logQ && !logQDraft}
                className="key-btn-secondary inline-flex justify-center disabled:opacity-40 sm:px-6"
              >
                Clear
              </button>
            </div>
          </form>
          <div className="overflow-x-auto rounded-xl border border-outline-variant/15">
            <table className="w-full border-collapse text-left font-mono text-xs">
              <thead className="bg-surface-container-high/80 text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">
                <tr>
                  {LOG_SORT_COLUMNS.map((col) => (
                    <th key={col.key} className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => onSortColumn(col.key)}
                        className="flex items-center text-left font-bold uppercase tracking-wide text-on-surface-variant hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-container/40"
                      >
                        {col.label}
                        <SortIndicator active={filters.logSort === col.key} dir={filters.logDir} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10 text-on-surface">
                {logs.map((row) => (
                  <tr key={row.id} className="bg-surface-container-lowest/50">
                    <td className="whitespace-nowrap px-3 py-2 text-on-surface-variant">{row.occurredAt}</td>
                    <td className="max-w-xs truncate px-3 py-2">{row.path}</td>
                    <td className="whitespace-nowrap px-3 py-2">{row.kind}</td>
                    <td className="px-3 py-2">{row.method}</td>
                    <td className="px-3 py-2">{row.ip}</td>
                    <td className="max-w-[14rem] truncate px-3 py-2 text-on-surface-variant">
                      {row.location ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-on-surface-variant">
                      <button
                        type="button"
                        onClick={() => {
                          setLogQDraft(row.fingerprint)
                          logSearchInputRef.current?.focus()
                        }}
                        title="Use fingerprint in log search"
                        className="cursor-pointer rounded font-mono text-left text-on-surface-variant underline decoration-outline-variant/50 underline-offset-2 hover:text-primary hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-container/40"
                      >
                        {row.fingerprintPreview}…
                      </button>
                    </td>
                    <td className="px-3 py-2">{row.userId ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={filters.page <= 1}
              className="key-btn-secondary disabled:opacity-40"
              onClick={(e) => goToPage(e, filters.page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={filters.page >= filters.totalPages}
              className="key-btn-secondary disabled:opacity-40"
              onClick={(e) => goToPage(e, filters.page + 1)}
            >
              Next
            </button>
          </div>
        </section>
      </div>
    </>
  )
}
